// Dynamic theme system for multi-tenant frontend
// Add this to: evv-frontend/src/hooks/useOrganizationTheme.js

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useVersionedApi } from '../utils/versionedApi';
import { apiLog } from '../utils/debugConfig';

import { safeStorage } from '../utils/safeStorage';
// Default AraSync theme (fallback)
const DEFAULT_THEME = {
  name: 'AraSync',
  branding: {
    name: 'AraSync',
    tagline: 'Multi-Tenant Electronic Visit Verification',
    colors: {
      primary: '#1C8A7D',    // AraSync Teal Green
      secondary: '#4FD1C5',  // AraSync Light Aqua
      accent: '#0D2538'      // AraSync Navy Blue
    },
    logo: null, // No logo file needed
  }
};

// Theme configurations for different organizations
const THEME_CONFIGS = {
  // AraSync Platform Theme (for platform admins like admin@arasync.com)
  'arasync-platform': {
    colors: {
      primary: '#1C8A7D',        // Teal Green
      'primary-light': '#4FD1C5',  // Light Aqua
      'primary-dark': '#0D2538',   // Navy Blue
      secondary: '#4FD1C5',       // Light Aqua
      accent: '#F5F7FA',          // Neutral Gray
      neutral: '#F5F7FA',         // Neutral Gray
    },
    cssVars: {
      '--color-primary': '#1C8A7D',
      '--color-primary-light': '#4FD1C5',
      '--color-secondary': '#4FD1C5',
      '--org-primary': '#1C8A7D',
      '--org-secondary': '#4FD1C5',
      '--org-accent': '#F5F7FA',
      '--org-primary-dark': '#0D2538', // Navy Blue for dark mode
    }
  },
  'wecare-health': {
    colors: {
      primary: '#D4711A',
      'primary-light': '#FBB040',
      'primary-dark': '#E8851A',
      secondary: '#F9B233',
      accent: '#F9B233',
    },
    cssVars: {
      '--color-primary': '#D4711A',
      '--color-primary-light': '#FBB040',
      '--color-secondary': '#F9B233',
      '--org-primary': '#D4711A',
      '--org-secondary': '#2563EB',
      '--org-accent': '#F9B233',
      '--org-primary-dark': '#B85C13', // Darker variant for dark mode
    }
  },
  'sunshine-care': {
    colors: {
      primary: '#F59E0B',
      'primary-light': '#FCD34D',
      'primary-dark': '#D97706',
      secondary: '#10B981',
      accent: '#34D399',
    },
    cssVars: {
      '--color-primary': '#F59E0B',
      '--color-primary-light': '#FCD34D',
      '--color-secondary': '#10B981',
      '--org-primary': '#F59E0B',
      '--org-secondary': '#10B981',
      '--org-accent': '#34D399',
      '--org-primary-dark': '#D97706', // Darker amber variant for dark mode
    }
  },
  'family-first-care': {
    colors: {
      primary: '#DC2626',
      'primary-light': '#EF4444',
      'primary-dark': '#B91C1C',
      secondary: '#2563EB',
      accent: '#3B82F6',
    },
    cssVars: {
      '--color-primary': '#DC2626',
      '--color-primary-light': '#EF4444',
      '--color-secondary': '#2563EB',
      '--org-primary': '#DC2626',
      '--org-secondary': '#2563EB',
      '--org-accent': '#3B82F6',
      '--org-primary-dark': '#B91C1C', // Darker red variant for dark mode
    }
  },
  'nova-health': {
    colors: {
      primary: '#6366F1',
      'primary-light': '#818CF8',
      'primary-dark': '#4F46E5',
      secondary: '#8B5CF6',
      accent: '#A78BFA',
    },
    cssVars: {
      '--color-primary': '#6366F1',
      '--color-primary-light': '#818CF8',
      '--color-secondary': '#8B5CF6',
      '--org-primary': '#6366F1',
      '--org-secondary': '#8B5CF6',
      '--org-accent': '#A78BFA',
      '--org-primary-dark': '#3730A3', // Darker variant for dark mode
    }
  },
  'scpd-coresponder': {
    colors: {
      primary: '#1E3A5F',        // Police Navy Blue
      'primary-light': '#2C5282',  // Lighter Navy
      'primary-dark': '#1A365D',   // Darker Navy
      secondary: '#C9A227',       // Gold/Badge color
      accent: '#FFFFFF',          // White
    },
    cssVars: {
      '--color-primary': '#1E3A5F',
      '--color-primary-light': '#2C5282',
      '--color-secondary': '#C9A227',
      '--org-primary': '#1E3A5F',
      '--org-secondary': '#C9A227',
      '--org-accent': '#FFFFFF',
      '--org-primary-dark': '#1A365D', // Darker navy for dark mode
    }
  }
};

const ORGANIZATION_NAMES = {
  'wecare-health': 'WeCare',
  'sunshine-care': 'Sunshine Care',
  'sunrise-health': 'Sunrise Health',
  'family-first-care': 'Family First Care',
  'nova-health': 'Nova Health',
  'arasync-platform': 'AraSync',
  'scpd-coresponder': 'SCPD CoResponder',
};

const ORGANIZATION_ID_SLUGS = {
  'ea39898f-5f2f-4e30-976e-3611ba4197d9': 'wecare-health',
  '51fb845f-53ee-4794-bf54-81e575bfe8ba': 'sunshine-care',
  '4dd1273b-3c37-4617-a0b8-dedcbace80e1': 'sunrise-health',
  '27ff73f4-1684-44f8-bcf0-63427a16a9f3': 'family-first-care',
  '98ccfb90-4106-438f-8469-43cf6a863afd': 'nova-health',
  'scpd-coresponder-org': 'scpd-coresponder',
};

const readStoredJson = (key) => {
  try {
    return JSON.parse(safeStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const decodeStoredToken = () => {
  try {
    const token = safeStorage.getItem('evv_token');
    const payload = token?.split('.')?.[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

/**
 * Resolve the current tenant from authenticated session data before using
 * legacy static mappings. New organizations therefore work without a code
 * change as soon as their organization slug is returned at login.
 */
export const resolveOrganizationIdentity = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const orgParam = urlParams.get('org');
  if (orgParam) {
    safeStorage.setItem('dev_organization', orgParam);
    return { slug: orgParam, name: null };
  }

  const storedUser = readStoredJson('evv_user');
  if (storedUser?.role === 'super_admin' && !storedUser?.organizationId) {
    return { slug: 'arasync-platform', name: 'AraSync' };
  }

  const storedOrganization = readStoredJson('evv_organization') || storedUser?.organization;
  const storedSlug = storedOrganization?.slug || storedUser?.organizationSlug;
  if (storedSlug) {
    return {
      slug: storedSlug,
      name: storedOrganization?.name || storedUser?.organizationName || null,
    };
  }

  const decoded = decodeStoredToken();
  if (decoded?.role === 'super_admin' && !decoded?.organizationId) {
    return { slug: 'arasync-platform', name: 'AraSync' };
  }
  if (decoded?.organizationId) {
    const slug = ORGANIZATION_ID_SLUGS[decoded.organizationId] || decoded.organizationSlug || decoded.organizationId;
    return { slug, name: ORGANIZATION_NAMES[slug] || null };
  }

  const hostname = window.location.hostname;
  const subdomainMatch = hostname.match(/^([^.]+)\.evvapp\.com$/);
  if (subdomainMatch) return { slug: subdomainMatch[1], name: null };

  const azureMatch = hostname.match(/^([^-]+)-frontend\..*\.azurecontainerapps\.io$/);
  if (azureMatch) return { slug: azureMatch[1], name: null };

  const slug = safeStorage.getItem('dev_organization') || 'arasync-platform';
  return { slug, name: ORGANIZATION_NAMES[slug] || null };
};

const buildThemeData = (identity, organization = null) => {
  const staticConfig = THEME_CONFIGS[identity.slug];
  const remoteBranding = organization?.branding || {};
  const remoteColors = remoteBranding.colors || {};
  const slug = organization?.slug || identity.slug;
  const colors = {
    ...DEFAULT_THEME.branding.colors,
    ...(staticConfig?.colors || {}),
    ...remoteColors,
  };

  if (remoteBranding.primaryColor) colors.primary = remoteBranding.primaryColor;
  if (remoteBranding.secondaryColor) colors.secondary = remoteBranding.secondaryColor;
  if (remoteBranding.accentColor) colors.accent = remoteBranding.accentColor;

  const name = remoteBranding.name
    || organization?.name
    || identity.name
    || ORGANIZATION_NAMES[identity.slug]
    || 'AraSync';
  const logo = null;

  return {
    name,
    slug,
    logo,
    logoUrl: logo,
    branding: {
      ...DEFAULT_THEME.branding,
      ...remoteBranding,
      name,
      tagline: remoteBranding.tagline
        || (identity.slug === 'arasync-platform'
          ? DEFAULT_THEME.branding.tagline
          : 'Electronic Health Record'),
      colors,
      logo,
      logoUrl: logo,
    },
  };
};

// Helper function to adjust color brightness
const adjustColorBrightness = (hex, percent) => {
  // Remove # if present
  const color = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  
  // Adjust brightness
  const factor = (100 + percent) / 100;
  const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
  const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
  const newB = Math.min(255, Math.max(0, Math.round(b * factor)));
  
  // Convert back to hex
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

export const useOrganizationTheme = () => {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiClient = useVersionedApi('v1');

  // Apply theme to document
  const applyTheme = useCallback((branding, orgSlug) => {
    const themeConfig = THEME_CONFIGS[orgSlug];
    const root = document.documentElement;
    
    if (branding?.colors) {
      const primaryColor = branding.colors.primary || branding.primaryColor || '#1C8A7D';
      const secondaryColor = branding.colors.secondary || branding.secondaryColor || '#4FD1C5';
      const accentColor = branding.colors.accent || branding.accentColor || secondaryColor;
      const primaryLight = branding.colors['primary-light'] || branding.colors.primaryLight || secondaryColor;
      
      // Generate darker variant for dark mode (20% darker)
      const primaryDark = adjustColorBrightness(primaryColor, -20);
      
      // Set CSS custom properties for dynamic theming
      const dynamicCssVars = {
        '--color-primary': primaryColor,
        '--color-primary-light': primaryLight,
        '--color-secondary': secondaryColor,
        '--org-primary': primaryColor,
        '--org-secondary': secondaryColor,
        '--org-accent': accentColor,
        '--org-primary-dark': primaryDark, // Generated dark variant
      };
      
      Object.entries(dynamicCssVars).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });
    } else if (themeConfig?.cssVars) {
      Object.entries(themeConfig.cssVars).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });
    } else {
      // Fallback to default AraSync platform theme
      console.warn('🎨 No theme config or API colors found for:', orgSlug, 'using AraSync platform default');
      const defaultCssVars = THEME_CONFIGS['arasync-platform'].cssVars;
      Object.entries(defaultCssVars).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });
    }

    // The org name is only the fallback title: a page title already set by
    // usePageTitle ("WeCare Home Health - AraSync") stays when the theme
    // loads after it.
    if (!document.title || document.title === 'AraSync') {
      document.title = branding?.name ? `${branding.name} | AraSync` : 'AraSync';
    }

    // Update favicon if provided
    if (branding?.favicon) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = branding.favicon;
      }
    }

    // Add theme class to body
    document.body.className = document.body.className.replace(/theme-[^\s]+/g, '');
    document.body.classList.add(`theme-${orgSlug}`);
  }, []); // No dependencies needed for this function

  const organizationIdentity = useMemo(() => resolveOrganizationIdentity(), []);
  const organizationSlug = organizationIdentity.slug;

  useEffect(() => {
    const fetchOrganizationTheme = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/organizations/${encodeURIComponent(organizationSlug)}/theme`);
        const themeData = buildThemeData(organizationIdentity, response.data);
        apiLog('🏢 Loaded organization theme:', {
          orgSlug: organizationSlug,
          orgName: themeData.branding.name,
        });
        
        setTheme(themeData);
        applyTheme(themeData.branding, organizationSlug);
        
        setError(null);
      } catch (err) {
        console.warn('Failed to load organization theme; using tenant fallback', {
          status: err?.response?.status || null,
          code: err?.code || null,
        });
        setError(err);
        const fallbackTheme = buildThemeData(organizationIdentity);
        applyTheme(fallbackTheme.branding, organizationSlug);
        setTheme(fallbackTheme);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationTheme();
  }, [apiClient, applyTheme, organizationIdentity, organizationSlug]);
  
  return {
    theme,
    loading,
    error,
    organizationSlug,
    hasFeature: (featureName) => {
      return theme?.branding?.features?.includes(featureName) || false;
    },
    getColor: (colorName) => {
      const liveColors = theme?.branding?.colors || {};
      const themeConfig = THEME_CONFIGS[organizationSlug] || THEME_CONFIGS['arasync-platform'];
      return liveColors[colorName]
        || liveColors.primary
        || themeConfig?.colors?.[colorName]
        || themeConfig?.colors?.primary
        || '#1C8A7D';
    }
  };
};
